import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { Heart, Zap, EyeOff } from "lucide-react"

type Props = {
  annual_amount:   number
  lifetime_amount: number
  signed_in:       boolean
}

function formatAUD(cents: number) {
  return `$${(cents / 100).toFixed(0)}`
}

function TierCard({
  title,
  amount,
  description,
  onSelect,
  signedIn,
}: {
  title: string
  amount: number
  description: string
  onSelect: () => void
  signedIn: boolean
}) {
  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 flex flex-col gap-4">
      <div>
        <h2 className="mb-1">{title}</h2>
        <p className="text-3xl font-bold text-ink-body">{formatAUD(amount)}</p>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-2">{description}</p>
      </div>
      {signedIn ? (
        <Button onClick={onSelect} className="mt-auto">Donate {formatAUD(amount)}</Button>
      ) : (
        <Link href="/signup" className="mt-auto">
          <Button variant="secondary" className="w-full">Create an account to donate</Button>
        </Link>
      )}
    </div>
  )
}

export default function Donate({ annual_amount, lifetime_amount, signed_in }: Props) {
  const title = "Support the Australian Music Database"
  const description = "Help keep the Australian Music Database free and independent. A one-time donation removes ads for a year or forever."

  async function donate(amountCents: number) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const res = await fetch("/donations", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body: JSON.stringify({ amount_cents: amountCents }),
    })
    const { url } = await res.json()
    window.location.href = url
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      <PublicShell>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
              <Heart className="w-5 h-5 text-amber-600 dark:text-amber-400 fill-current" />
            </div>
            <h1 className="mb-0">Support the database</h1>
          </div>

          <p className="text-stone-600 dark:text-stone-400 mb-6 leading-relaxed">
            The Australian Music Database is free, independent, and run by music fans —
            no investors, no paywalls, no agenda beyond getting the history right.
            Keeping it online costs money. A small one-time donation helps.
          </p>

          {/* ── What your support does ──────────────────────────────── */}
          <div className="rounded-xl border border-hairline bg-surface p-5 mb-8">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <EyeOff className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                <span className="text-sm text-ink-body">
                  <strong>Removes ads</strong> across the entire site — for one year or forever, depending on your tier.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Zap className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                <span className="text-sm text-ink-body">
                  <strong>Keeps the lights on</strong> — hosting, development, and the ongoing research it takes to keep records accurate.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Heart className="w-4 h-4 mt-0.5 text-amber-500 shrink-0 fill-current" />
                <span className="text-sm text-ink-body">
                  <strong>Gets you on the{" "}
                  <Link href="/wall-of-fame" className="underline">Wall of Fame</Link>
                  </strong> — a small but permanent thank-you, if you'd like it.
                </span>
              </li>
            </ul>
          </div>

          {/* ── Tier cards ──────────────────────────────────────────── */}
          <div className="grid sm:grid-cols-2 gap-6">
            <TierCard
              title="Ad-free for one year"
              amount={annual_amount}
              description="Remove ads across the entire site for 12 months. Renew early — we'll add the year on top of your remaining time."
              signedIn={signed_in}
              onSelect={() => donate(annual_amount)}
            />
            <TierCard
              title="Ad-free forever"
              amount={lifetime_amount}
              description="A one-time donation that removes ads permanently. Thank you — you're a legend."
              signedIn={signed_in}
              onSelect={() => donate(lifetime_amount)}
            />
          </div>

          {!signed_in && (
            <p className="text-sm text-stone-500 dark:text-stone-400 text-center mt-8">
              Don't have an account?{" "}
              <Link href="/signup" className="underline">Create one for free</Link> — it takes 30 seconds.
            </p>
          )}

          <p className="text-xs text-stone-400 dark:text-stone-500 text-center mt-6">
            Payments processed securely by Stripe. We never see your card details.
          </p>
        </div>
      </PublicShell>
    </>
  )
}
